import { Field } from 'formik';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/tailwindui/fieldset';
import { Field as FieldComponent } from '@/src/tailwindui/fieldset';
import { Textarea } from '@/src/tailwindui/textarea';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism.css';
import { EmailTemplate } from '../../EmailTemplates/components/hooks/useTemplates';

interface SharingSettingsProps {
    values: any;
    setFieldValue: (field: string, value: any) => void;
    templates: EmailTemplate[];
    selectedTemplate: EmailTemplate | null;
    setSelectedTemplate: (template: EmailTemplate | null) => void;
}

export default function SharingSettings({
    values,
    setFieldValue,
    templates,
    selectedTemplate,
    setSelectedTemplate,
}: SharingSettingsProps) {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Sharing Settings
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Configure how users can share photos and the messaging
                    experience.
                </p>
            </div>

            {/* Sharing Options */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Sharing Options
                    </h4>
                </div>

                <FieldComponent>
                    <div className="flex items-center space-x-2">
                        <Field
                            id="sendLinkToPhoto"
                            name="sendLinkToPhoto"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <Label
                            htmlFor="sendLinkToPhoto"
                            className="text-sm font-medium"
                        >
                            Send link to individual photo
                        </Label>
                    </div>
                </FieldComponent>

                <FieldComponent>
                    <div className="flex items-center space-x-2">
                        <Field
                            id="showGalleryLink"
                            name="showGalleryLink"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <Label
                            htmlFor="showGalleryLink"
                            className="text-sm font-medium"
                        >
                            Show gallery link in sharing
                        </Label>
                    </div>
                </FieldComponent>
            </div>

            {/* Text Sharing */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Text Sharing
                    </h4>
                </div>

                <FieldComponent>
                    <div className="flex items-center space-x-2">
                        <Field
                            id="textEnabled"
                            name="textEnabled"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <Label
                            htmlFor="textEnabled"
                            className="text-sm font-medium"
                        >
                            Enable text sharing
                        </Label>
                    </div>
                </FieldComponent>

                {values.textEnabled && (
                    <FieldComponent>
                        <Label htmlFor="textBody">Text Message</Label>
                        <Field
                            as={Textarea}
                            id="textBody"
                            name="textBody"
                            rows={4}
                            placeholder="Enter the default text message for sharing"
                        />
                    </FieldComponent>
                )}
            </div>

            {/* WhatsApp Sharing */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        WhatsApp Sharing
                    </h4>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Enable WhatsApp sharing for international guests.
                    </p>
                </div>

                <FieldComponent>
                    <div className="flex items-center space-x-2">
                        <Field
                            id="whatsappEnabled"
                            name="whatsappEnabled"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <Label
                            htmlFor="whatsappEnabled"
                            className="text-sm font-medium"
                        >
                            Enable WhatsApp sharing
                        </Label>
                    </div>
                </FieldComponent>
            </div>

            {/* Email Sharing */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Email Sharing
                    </h4>
                </div>

                <FieldComponent>
                    <div className="flex items-center space-x-2">
                        <Field
                            id="emailEnabled"
                            name="emailEnabled"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <Label
                            htmlFor="emailEnabled"
                            className="text-sm font-medium"
                        >
                            Enable email sharing
                        </Label>
                    </div>
                </FieldComponent>

                {values.emailEnabled && (
                    <div className="space-y-4">
                        <FieldComponent>
                            <Label htmlFor="fromAddress">
                                From Email Address
                            </Label>
                            <Field
                                as={Input}
                                id="fromAddress"
                                name="fromAddress"
                                type="email"
                                placeholder="noreply@example.com"
                            />
                        </FieldComponent>

                        <FieldComponent>
                            <Label htmlFor="fromName">From Name</Label>
                            <Field
                                as={Input}
                                id="fromName"
                                name="fromName"
                                placeholder="Your Gallery"
                            />
                        </FieldComponent>

                        <FieldComponent>
                            <Label htmlFor="subject">Email Subject</Label>
                            <Field
                                as={Input}
                                id="subject"
                                name="subject"
                                placeholder="You have new photos!"
                            />
                        </FieldComponent>

                        <FieldComponent>
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="sendAttachments"
                                    name="sendAttachments"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Label
                                    htmlFor="sendAttachments"
                                    className="text-sm font-medium"
                                >
                                    Send Attachments
                                </Label>
                            </div>
                        </FieldComponent>

                        <FieldComponent>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="emailBody">
                                        Email Body
                                    </Label>
                                    <div className="flex items-center space-x-2">
                                        <select
                                            className="text-sm border border-gray-300 rounded-md px-3 py-1"
                                            onChange={(e) =>
                                                setSelectedTemplate(
                                                    templates.find(
                                                        (template) =>
                                                            template.id ===
                                                            +e.currentTarget
                                                                .value,
                                                    ) ?? null,
                                                )
                                            }
                                        >
                                            <option value="">
                                                Select template
                                            </option>
                                            {templates.map((template) => (
                                                <option
                                                    key={template.id}
                                                    value={template.id}
                                                >
                                                    {template.name}
                                                </option>
                                            ))}
                                        </select>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                if (selectedTemplate) {
                                                    setFieldValue(
                                                        'emailBody',
                                                        selectedTemplate?.body,
                                                    );
                                                }
                                            }}
                                        >
                                            Apply Template
                                        </Button>
                                    </div>
                                </div>
                                <div className="border border-gray-300 rounded-md overflow-hidden">
                                    <Editor
                                        value={values.emailBody}
                                        onValueChange={(code) =>
                                            setFieldValue('emailBody', code)
                                        }
                                        highlight={(code) =>
                                            Prism.highlight(
                                                code,
                                                Prism.languages.html,
                                                'html',
                                            )
                                        }
                                        padding={10}
                                        style={{
                                            fontFamily:
                                                '"Fira code", "Fira Mono", monospace',
                                            fontSize: 14,
                                            minHeight: '200px',
                                        }}
                                    />
                                </div>
                            </div>
                        </FieldComponent>
                    </div>
                )}
            </div>

            {/* Share Description */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Share Description
                    </h4>
                </div>

                <FieldComponent>
                    <Label htmlFor="createShareDescriptionText">
                        Description Text
                    </Label>
                    <Field
                        as={Input}
                        id="createShareDescriptionText"
                        name="createShareDescriptionText"
                        placeholder="Enter description text for sharing"
                    />
                </FieldComponent>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FieldComponent>
                        <Label htmlFor="createShareDescriptionTextSize">
                            Font Size
                        </Label>
                        <Field
                            as={Input}
                            id="createShareDescriptionTextSize"
                            name="createShareDescriptionTextSize"
                            type="number"
                            placeholder="16"
                        />
                    </FieldComponent>

                    <FieldComponent>
                        <Label htmlFor="createShareDescriptionTextFont">
                            Font Family
                        </Label>
                        <Field
                            as={Input}
                            id="createShareDescriptionTextFont"
                            name="createShareDescriptionTextFont"
                            placeholder="Arial, sans-serif"
                        />
                    </FieldComponent>
                </div>
            </div>
        </div>
    );
}
